import {AgentType, ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';

export interface RealtimeVoiceChatHandoff {
  chatSessionId: string;
  chatType: ChatType;
  initialAgentType: AgentType;
}

interface PendingVoiceChatIntent {
  cancelled: boolean;
  handoff: RealtimeVoiceChatHandoff | null;
  promise: Promise<RealtimeVoiceChatHandoff | null>;
}

interface PendingVoiceChatCreation {
  scopeId: string;
  operationId: number;
  creationPromise: Promise<RealtimeVoiceChatHandoff | null>;
  handoff: RealtimeVoiceChatHandoff | null;
  activeIntent: PendingVoiceChatIntent;
}

let nextScopeId = 0;
let nextOperationId = 0;
let pendingCreation: PendingVoiceChatCreation | null = null;
let pendingStart: {scopeId: string; chatSessionId: string} | null = null;

function createVoiceChatIntent(operation: PendingVoiceChatCreation): PendingVoiceChatIntent {
  const intent: PendingVoiceChatIntent = {
    cancelled: false,
    handoff: null,
    promise: Promise.resolve(null),
  };
  intent.promise = operation.creationPromise.then(
    (handoff) => {
      if (handoff == null || intent.cancelled || pendingCreation?.operationId !== operation.operationId) {
        return null;
      }
      intent.handoff = {...handoff};
      return intent.handoff;
    },
    (error: unknown) => {
      if (intent.cancelled) {
        return null;
      }
      throw error;
    }
  );
  return intent;
}

export function createRealtimeVoiceChatHandoffScope(): string {
  nextScopeId += 1;
  return `realtime-voice-handoff-${nextScopeId}`;
}

export function createRealtimeVoiceChatHandoff(
  initialAgentType: AgentType,
  scopeId: string,
  createChat: () => Promise<{chatSessionId: string; chatType: ChatType}>
): Promise<RealtimeVoiceChatHandoff | null> {
  if (pendingCreation != null) {
    if (pendingCreation.scopeId !== scopeId) {
      return Promise.resolve(null);
    }
    if (!pendingCreation.activeIntent.cancelled) {
      return pendingCreation.activeIntent.promise;
    }
    pendingCreation.activeIntent = createVoiceChatIntent(pendingCreation);
    return pendingCreation.activeIntent.promise;
  }

  const operationId = ++nextOperationId;
  const operation: PendingVoiceChatCreation = {
    scopeId,
    operationId,
    creationPromise: Promise.resolve(null),
    handoff: null,
    activeIntent: {
      cancelled: false,
      handoff: null,
      promise: Promise.resolve(null),
    },
  };
  operation.creationPromise = createChat()
    .then(({chatSessionId, chatType}) => {
      if (pendingCreation?.operationId !== operationId) {
        return null;
      }
      operation.handoff = {chatSessionId, chatType, initialAgentType};
      return operation.handoff;
    })
    .catch((error: unknown) => {
      if (pendingCreation?.operationId === operationId) {
        pendingCreation = null;
      }
      throw error;
    });
  operation.activeIntent = createVoiceChatIntent(operation);
  pendingCreation = operation;
  return operation.activeIntent.promise;
}

export function getPendingRealtimeVoiceChatHandoff(
  scopeId: string
): Promise<RealtimeVoiceChatHandoff | null> | null {
  if (pendingCreation?.scopeId !== scopeId || pendingCreation.activeIntent.cancelled) {
    return null;
  }
  return pendingCreation.activeIntent.promise;
}

export function hasUnclaimedRealtimeVoiceChatHandoff(scopeId: string): boolean {
  return pendingCreation?.scopeId === scopeId;
}

export function cancelRealtimeVoiceChatHandoff(scopeId: string): void {
  if (pendingCreation?.scopeId === scopeId) {
    pendingCreation.activeIntent.cancelled = true;
  }
  if (pendingStart?.scopeId === scopeId) {
    pendingStart = null;
  }
}

export function claimRealtimeVoiceChatActivation(
  scopeId: string,
  handoff: RealtimeVoiceChatHandoff
): boolean {
  if (
    pendingCreation?.scopeId !== scopeId ||
    pendingCreation.activeIntent.cancelled ||
    pendingCreation.activeIntent.handoff !== handoff
  ) {
    return false;
  }
  pendingCreation = null;
  pendingStart = {scopeId, chatSessionId: handoff.chatSessionId};
  return true;
}

export function claimRealtimeVoiceStart(chatSessionId: string | null): boolean {
  if (chatSessionId == null || pendingStart?.chatSessionId !== chatSessionId) {
    return false;
  }
  pendingStart = null;
  return true;
}

export function resetRealtimeVoiceChatHandoffForTesting(): void {
  if (pendingCreation != null) {
    pendingCreation.activeIntent.cancelled = true;
  }
  pendingCreation = null;
  pendingStart = null;
}
